import { v4 as uuidv4 } from 'uuid';
import {
    EMAIL_BLOCK_TYPE,
    EMAIL_BUTTON_BINDING,
    EMAIL_DESIGN_SCHEMA_VERSION,
    DEFAULT_EMAIL_DESIGN_STYLES,
} from '@constants/campaignTemplates';

export const createBlock = type => {
    const base = { id: uuidv4(), type };
    switch (type) {
        case EMAIL_BLOCK_TYPE.HEADING:
            return { ...base, text: '', align: 'center' };
        case EMAIL_BLOCK_TYPE.TEXT:
            return { ...base, html: '', align: 'left' };
        case EMAIL_BLOCK_TYPE.IMAGE:
            return { ...base, url: '', alt: '', align: 'center' };
        case EMAIL_BLOCK_TYPE.BUTTON:
            return { ...base, label: '', binding: EMAIL_BUTTON_BINDING.URL, url: '', align: 'center' };
        case EMAIL_BLOCK_TYPE.COLUMNS:
            return { ...base, left: [], right: [] };
        case EMAIL_BLOCK_TYPE.SPACER:
            return { ...base, height: 24 };
        default:
            return base;
    }
};

const isColumns = block => block.type === EMAIL_BLOCK_TYPE.COLUMNS;

export const findBlock = (blocks, id) => {
    for (const block of blocks) {
        if (block.id === id) return block;
        if (!isColumns(block)) continue;
        const child = [...(block.left || []), ...(block.right || [])].find(c => c.id === id);
        if (child) return child;
    }
    return null;
};

export const findBlockLocation = (blocks, id) => {
    const index = blocks.findIndex(block => block.id === id);
    if (index !== -1) return { containerId: null, side: null, index };
    for (const block of blocks) {
        if (!isColumns(block)) continue;
        for (const side of ['left', 'right']) {
            const childIndex = (block[side] || []).findIndex(c => c.id === id);
            if (childIndex !== -1) return { containerId: block.id, side, index: childIndex };
        }
    }
    return null;
};

export const updateBlockById = (blocks, id, patch) =>
    blocks.map(block => {
        if (block.id === id) return { ...block, ...patch };
        if (!isColumns(block)) return block;
        return {
            ...block,
            left: (block.left || []).map(c => (c.id === id ? { ...c, ...patch } : c)),
            right: (block.right || []).map(c => (c.id === id ? { ...c, ...patch } : c)),
        };
    });

export const removeBlockById = (blocks, id) =>
    blocks
        .filter(block => block.id !== id)
        .map(block =>
            isColumns(block)
                ? {
                      ...block,
                      left: (block.left || []).filter(c => c.id !== id),
                      right: (block.right || []).filter(c => c.id !== id),
                  }
                : block,
        );

export const cloneBlockWithNewIds = block => ({
    ...block,
    id: uuidv4(),
    ...(isColumns(block)
        ? {
              left: (block.left || []).map(c => ({ ...c, id: uuidv4() })),
              right: (block.right || []).map(c => ({ ...c, id: uuidv4() })),
          }
        : {}),
});

export const createEmptyDesign = () => ({
    schemaVersion: EMAIL_DESIGN_SCHEMA_VERSION,
    styles: { ...DEFAULT_EMAIL_DESIGN_STYLES },
    blocks: [
        createBlock(EMAIL_BLOCK_TYPE.LOGO),
        createBlock(EMAIL_BLOCK_TYPE.HEADING),
        createBlock(EMAIL_BLOCK_TYPE.TEXT),
    ],
});

const withEnsuredId = block => ({ ...block, id: block.id || uuidv4() });

export const normalizeDesign = design => ({
    schemaVersion: design?.schemaVersion || EMAIL_DESIGN_SCHEMA_VERSION,
    styles: { ...DEFAULT_EMAIL_DESIGN_STYLES, ...(design?.styles || {}) },
    blocks: (design?.blocks || []).map(block =>
        isColumns(block)
            ? {
                  ...withEnsuredId(block),
                  left: (block.left || []).map(withEnsuredId),
                  right: (block.right || []).map(withEnsuredId),
              }
            : withEnsuredId(block),
    ),
});

export const BLOCK_LABEL_KEYS = {
    [EMAIL_BLOCK_TYPE.LOGO]: 'TemplateLibrary.blockLogo',
    [EMAIL_BLOCK_TYPE.IMAGE]: 'TemplateLibrary.blockImage',
    [EMAIL_BLOCK_TYPE.HEADING]: 'TemplateLibrary.blockHeading',
    [EMAIL_BLOCK_TYPE.TEXT]: 'TemplateLibrary.blockText',
    [EMAIL_BLOCK_TYPE.BUTTON]: 'TemplateLibrary.blockButton',
    [EMAIL_BLOCK_TYPE.DIVIDER]: 'TemplateLibrary.blockDivider',
    [EMAIL_BLOCK_TYPE.SPACER]: 'TemplateLibrary.blockSpacer',
    [EMAIL_BLOCK_TYPE.COLUMNS]: 'TemplateLibrary.blockColumns',
    [EMAIL_BLOCK_TYPE.SOCIAL]: 'TemplateLibrary.blockSocial',
};
